/**
 * Landlord Onboarding Metrics Component
 * Displays analytics and KPIs for landlord onboarding performance
 */

import React from 'react';
import {
  Box,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  VStack,
  HStack,
  Flex,
  Badge,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import {
  MdOutlineSpeed,
  MdOutlineCheckCircle,
  // MdOutlineTrendingUp,
  MdOutlineEmail,
  MdOutlineLogin,
  MdOutlineThumbUp,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import type { LandlordOnboardingStats } from '../../../types/landlordOnboarding';
// import { getNPSCategory } from '../../../types/landlordOnboarding';

interface LandlordOnboardingMetricsProps {
  stats: LandlordOnboardingStats | null;
}

const LandlordOnboardingMetrics: React.FC<LandlordOnboardingMetricsProps> = ({
  stats,
}) => {
  const { t } = useTranslation('landlordOnboarding');

  if (!stats) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">{t('metrics.noData')}</Text>
      </Box>
    );
  }

  // Calculate NPS
  const totalResponses = stats.promoters + stats.passives + stats.detractors;
  const npsScore = totalResponses > 0
    ? Math.round(((stats.promoters - stats.detractors) / totalResponses) * 100)
    : 0;

  const getNPSColor = (score: number): string => {
    if (score >= 50) return 'green';
    if (score >= 0) return 'orange';
    return 'red';
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        {/* Completion Rate */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel display="flex" alignItems="center" gap={2}>
                <Icon as={MdOutlineCheckCircle} color="green.500" />
                {t('metrics.completionRate')}
              </StatLabel>
              <StatNumber fontSize="3xl" color="green.500">
                {Math.round(stats.completionRate)}%
              </StatNumber>
              <StatHelpText>
                {stats.completedThisMonth} completed this month
              </StatHelpText>
              <Progress
                value={stats.completionRate}
                size="sm"
                colorScheme="green"
                mt={2}
              />
            </Stat>
          </CardBody>
        </Card>

        {/* Gmail Connection Rate */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel display="flex" alignItems="center" gap={2}>
                <Icon as={MdOutlineEmail} color="blue.500" />
                {t('metrics.gmailConnectionRate48h')}
              </StatLabel>
              <StatNumber fontSize="3xl" color="blue.500">
                {Math.round(stats.gmailConnectionRate48h)}%
              </StatNumber>
              <StatHelpText>
                {stats.gmailConnectionRate48h >= 90 ? (
                  <HStack spacing={1}>
                    <StatArrow type="increase" />
                    <Text>Above target (90%)</Text>
                  </HStack>
                ) : (
                  <HStack spacing={1}>
                    <StatArrow type="decrease" />
                    <Text>Below target (90%)</Text>
                  </HStack>
                )}
              </StatHelpText>
              <Progress
                value={stats.gmailConnectionRate48h}
                size="sm"
                colorScheme={stats.gmailConnectionRate48h >= 90 ? 'green' : 'orange'}
                mt={2}
              />
            </Stat>
          </CardBody>
        </Card>

        {/* First Login Rate */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel display="flex" alignItems="center" gap={2}>
                <Icon as={MdOutlineLogin} color="purple.500" />
                {t('metrics.firstLoginRate24h')}
              </StatLabel>
              <StatNumber fontSize="3xl" color="purple.500">
                {Math.round(stats.firstLoginRate24h)}%
              </StatNumber>
              <StatHelpText>
                {stats.firstLoginRate24h >= 80 ? (
                  <HStack spacing={1}>
                    <StatArrow type="increase" />
                    <Text>Above target (80%)</Text>
                  </HStack>
                ) : (
                  <HStack spacing={1}>
                    <StatArrow type="decrease" />
                    <Text>Below target (80%)</Text>
                  </HStack>
                )}
              </StatHelpText>
              <Progress
                value={stats.firstLoginRate24h}
                size="sm"
                colorScheme={stats.firstLoginRate24h >= 80 ? 'green' : 'orange'}
                mt={2}
              />
            </Stat>
          </CardBody>
        </Card>

        {/* NPS Score */}
        <Card>
          <CardBody>
            <Stat>
              <StatLabel display="flex" alignItems="center" gap={2}>
                <Icon as={MdOutlineThumbUp} color={`${getNPSColor(npsScore)}.500`} />
                {t('metrics.npsScore')}
              </StatLabel>
              <StatNumber fontSize="3xl" color={`${getNPSColor(npsScore)}.500`}>
                {npsScore}
              </StatNumber>
              <StatHelpText>
                Based on {totalResponses} responses
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Time Metrics */}
      <Card>
        <CardHeader>
          <Heading size="sm" display="flex" alignItems="center" gap={2}>
            <Icon as={MdOutlineSpeed} />
            {t('metrics.timeMetrics')}
          </Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel fontSize="sm">
                {t('metrics.avgTimeToFirstLogin')}
              </StatLabel>
              <StatNumber color="blue.600">
                {Math.round(stats.avgTimeToFirstLogin)}h
              </StatNumber>
              <StatHelpText>
                {stats.avgTimeToFirstLogin <= 24
                  ? t('metrics.excellent')
                  : stats.avgTimeToFirstLogin <= 48
                  ? t('metrics.good')
                  : t('metrics.needsImprovement')}
              </StatHelpText>
            </Stat>

            <Stat>
              <StatLabel fontSize="sm">
                {t('metrics.avgTimeToGmailConnection')}
              </StatLabel>
              <StatNumber color="green.600">
                {Math.round(stats.avgTimeToGmailConnection)}h
              </StatNumber>
              <StatHelpText>
                {stats.avgTimeToGmailConnection <= 48
                  ? t('metrics.excellent')
                  : stats.avgTimeToGmailConnection <= 72
                  ? t('metrics.good')
                  : t('metrics.needsImprovement')}
              </StatHelpText>
            </Stat>

            <Stat>
              <StatLabel fontSize="sm">
                {t('metrics.avgCompletionTime')}
              </StatLabel>
              <StatNumber color="purple.600">
                {stats.avgCompletionTime.toFixed(1)} days
              </StatNumber>
              <StatHelpText>
                {stats.avgCompletionTime <= 2
                  ? t('metrics.excellent')
                  : stats.avgCompletionTime <= 4
                  ? t('metrics.good')
                  : t('metrics.needsImprovement')}
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* NPS Breakdown */}
      <Card>
        <CardHeader>
          <Heading size="sm">{t('metrics.npsBreakdown')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Promoters */}
            <Box>
              <Flex justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="green">
                    {t('metrics.promoters')}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    Score 9-10
                  </Text>
                </HStack>
                <Text fontWeight="medium">
                  {stats.promoters} (
                  {totalResponses > 0
                    ? Math.round((stats.promoters / totalResponses) * 100)
                    : 0}
                  %)
                </Text>
              </Flex>
              <Progress
                value={
                  totalResponses > 0
                    ? (stats.promoters / totalResponses) * 100
                    : 0
                }
                size="sm"
                colorScheme="green"
              />
            </Box>

            {/* Passives */}
            <Box>
              <Flex justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="orange">
                    {t('metrics.passives')}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    Score 7-8
                  </Text>
                </HStack>
                <Text fontWeight="medium">
                  {stats.passives} (
                  {totalResponses > 0
                    ? Math.round((stats.passives / totalResponses) * 100)
                    : 0}
                  %)
                </Text>
              </Flex>
              <Progress
                value={
                  totalResponses > 0
                    ? (stats.passives / totalResponses) * 100
                    : 0
                }
                size="sm"
                colorScheme="orange"
              />
            </Box>

            {/* Detractors */}
            <Box>
              <Flex justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="red">
                    {t('metrics.detractors')}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    Score 0-6
                  </Text>
                </HStack>
                <Text fontWeight="medium">
                  {stats.detractors} (
                  {totalResponses > 0
                    ? Math.round((stats.detractors / totalResponses) * 100)
                    : 0}
                  %)
                </Text>
              </Flex>
              <Progress
                value={
                  totalResponses > 0
                    ? (stats.detractors / totalResponses) * 100
                    : 0
                }
                size="sm"
                colorScheme="red"
              />
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* By Stage */}
      <Card>
        <CardHeader>
          <Heading size="sm">{t('metrics.byStage')}</Heading>
        </CardHeader>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('metrics.stage')}</Th>
                <Th isNumeric>{t('metrics.count')}</Th>
                <Th>{t('metrics.distribution')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(stats.byStage).map(([stage, count]) => (
                <Tr key={stage}>
                  <Td>{t(`landlordOnboarding.stages.${stage}`)}</Td>
                  <Td isNumeric fontWeight="medium">
                    {count}
                  </Td>
                  <Td>
                    <Progress
                      value={
                        stats.totalOnboardings > 0
                          ? (count / stats.totalOnboardings) * 100
                          : 0
                      }
                      size="sm"
                      colorScheme="blue"
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* By Specialist */}
      <Card>
        <CardHeader>
          <Heading size="sm">
            {t('metrics.bySpecialist')}
          </Heading>
        </CardHeader>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('metrics.specialist')}</Th>
                <Th isNumeric>{t('metrics.onboardings')}</Th>
                <Th isNumeric>{t('metrics.completionRate')}</Th>
                <Th isNumeric>{t('metrics.avgNps')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(stats.bySpecialist)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([id, data]) => (
                  <Tr key={id}>
                    <Td fontWeight="medium">{data.name}</Td>
                    <Td isNumeric>{data.count}</Td>
                    <Td isNumeric>
                      <HStack spacing={2} justify="flex-end">
                        <Text>{Math.round(data.completionRate)}%</Text>
                        <Badge
                          colorScheme={
                            data.completionRate >= 90
                              ? 'green'
                              : data.completionRate >= 75
                              ? 'orange'
                              : 'red'
                          }
                        >
                          {data.completionRate >= 90
                            ? 'Excellent'
                            : data.completionRate >= 75
                            ? 'Good'
                            : 'Needs Work'}
                        </Badge>
                      </HStack>
                    </Td>
                    <Td isNumeric>
                      <HStack spacing={2} justify="flex-end">
                        <Text>{data.avgNps.toFixed(1)}</Text>
                        <Badge colorScheme={getNPSColor(data.avgNps)}>
                          {data.avgNps >= 50
                            ? 'Excellent'
                            : data.avgNps >= 0
                            ? 'Good'
                            : 'Poor'}
                        </Badge>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <Heading size="sm">{t('metrics.summary')}</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Stat>
              <StatLabel>{t('metrics.totalOnboardings')}</StatLabel>
              <StatNumber>{stats.totalOnboardings}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t('metrics.activeOnboardings')}</StatLabel>
              <StatNumber color="blue.500">{stats.activeOnboardings}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>
                {t('metrics.completedThisMonth')}
              </StatLabel>
              <StatNumber color="green.500">
                {stats.completedThisMonth}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t('metrics.blockedOnboardings')}</StatLabel>
              <StatNumber color="red.500">
                {stats.blockedOnboardings}
              </StatNumber>
            </Stat>
          </SimpleGrid>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default LandlordOnboardingMetrics;
